<template>
  <v-sheet>
    <v-container fluid>
      <!-- Header -->
      <v-row align="center" class="mb-1">
        <v-col>
          <span class="text-h6 font-weight-bold">Dashboard</span>
        </v-col>
        <v-col cols="auto">
          <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="getDashboardData" title="Refresh" />
        </v-col>
      </v-row>

      <!-- Loading skeleton -->
      <v-row v-if="loading">
        <v-col cols="12" sm="6" md="3" v-for="i in 8" :key="i">
          <v-skeleton-loader type="card" elevation="1" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Stat Cards -->
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="(card, index) in cards" :key="index">
            <v-card class="pa-4" elevation="2" :color="card.color" rounded="lg">
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="text-subtitle-2 text-white" style="opacity:0.85">{{ card.title }}</div>
                  <div class="text-h4 font-weight-bold text-white">{{ card.count }}</div>
                </div>
                <v-icon size="44" color="white" style="opacity:0.6">{{ card.icon }}</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Row -->
        <v-row class="mt-3">
          <!-- Gender Donut Chart -->
          <v-col cols="12" md="4">
            <v-card variant="outlined" class="pa-4" height="290">
              <div class="text-subtitle-1 font-weight-medium mb-2">Gender Split</div>
              <div class="d-flex justify-center align-center" style="height: 200px;">
                <template v-if="genderData.total > 0">
                  <div style="position:relative;">
                    <svg viewBox="0 0 200 200" width="190" height="190">
                      <circle cx="100" cy="100" r="70" fill="none" stroke="#e0e0e0" stroke-width="24" />
                      <!-- Male arc -->
                      <circle cx="100" cy="100" r="70" fill="none"
                        stroke="#1976D2" stroke-width="24" stroke-linecap="butt"
                        :stroke-dasharray="`${genderData.maleDash} ${genderData.circumference}`"
                        transform="rotate(-90 100 100)" />
                      <!-- Female arc -->
                      <circle cx="100" cy="100" r="70" fill="none"
                        stroke="#E91E63" stroke-width="24" stroke-linecap="butt"
                        :stroke-dasharray="`${genderData.femaleDash} ${genderData.circumference}`"
                        :stroke-dashoffset="-genderData.maleDash"
                        transform="rotate(-90 100 100)" />
                      <text x="100" y="95" text-anchor="middle" font-size="26" font-weight="bold" fill="#333">{{ genderData.total }}</text>
                      <text x="100" y="116" text-anchor="middle" font-size="11" fill="#888">attendees</text>
                    </svg>
                  </div>
                </template>
                <div v-else class="text-grey text-center">No attendee data</div>
              </div>
              <div class="d-flex justify-center mt-1" style="gap:20px;">
                <div class="d-flex align-center" style="gap:6px;">
                  <div style="width:12px;height:12px;border-radius:50%;background:#1976D2;flex-shrink:0;"></div>
                  <span class="text-caption">Male ({{ genderData.malePct }}%)</span>
                </div>
                <div class="d-flex align-center" style="gap:6px;">
                  <div style="width:12px;height:12px;border-radius:50%;background:#E91E63;flex-shrink:0;"></div>
                  <span class="text-caption">Female ({{ genderData.femalePct }}%)</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Top Events Bar Chart -->
          <v-col cols="12" md="8">
            <v-card variant="outlined" class="pa-4" height="290">
              <div class="text-subtitle-1 font-weight-medium mb-2">Top Events by Attendance</div>
              <div style="height: 225px; overflow: hidden;" class="d-flex align-end">
                <template v-if="topEventChart.length > 0">
                  <svg viewBox="0 0 500 190" width="100%" height="100%" preserveAspectRatio="none">
                    <!-- Grid lines -->
                    <line x1="0" y1="30"  x2="500" y2="30"  stroke="#f0f0f0" stroke-width="1" />
                    <line x1="0" y1="72"  x2="500" y2="72"  stroke="#f0f0f0" stroke-width="1" />
                    <line x1="0" y1="114" x2="500" y2="114" stroke="#f0f0f0" stroke-width="1" />
                    <!-- Baseline -->
                    <line x1="0" y1="156" x2="500" y2="156" stroke="#ccc" stroke-width="1" />
                    <!-- Bars -->
                    <g v-for="(bar, i) in topEventChart" :key="i">
                      <rect :x="bar.x" :y="bar.y" :width="bar.w" :height="bar.h" :fill="bar.color" rx="3" />
                      <text :x="bar.x + bar.w / 2" y="170" text-anchor="middle" font-size="9" fill="#666">{{ bar.label }}</text>
                      <text :x="bar.x + bar.w / 2" :y="bar.y - 4" text-anchor="middle" font-size="10" fill="#333" font-weight="bold">{{ bar.count }}</text>
                    </g>
                  </svg>
                </template>
                <div v-else class="d-flex align-center justify-center w-100 text-grey">No event data</div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Check-in Rates + Upcoming Events -->
        <v-row class="mt-3">
          <v-col cols="12" md="6">
            <v-card variant="outlined" class="pa-4">
              <div class="text-subtitle-1 font-weight-medium mb-3">Check-in Rates by Event</div>
              <template v-if="eventStats.length > 0">
                <div v-for="ev in eventStats" :key="ev.id" class="mb-4">
                  <div class="d-flex justify-space-between mb-1">
                    <span class="text-body-2 text-truncate" style="max-width: 70%;">{{ ev.name }}</span>
                    <span class="text-body-2 font-weight-bold">{{ ev.rate }}%</span>
                  </div>
                  <v-progress-linear
                    :model-value="ev.rate"
                    :color="ev.rate >= 75 ? 'success' : ev.rate >= 40 ? 'warning' : 'error'"
                    height="8"
                    rounded
                    bg-color="grey-lighten-3"
                  />
                  <div class="text-caption text-grey mt-1">{{ ev.checkedIn }} / {{ ev.total }} checked in</div>
                </div>
              </template>
              <div v-else class="text-grey text-center py-4">No data available</div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card variant="outlined">
              <div class="text-subtitle-1 font-weight-medium px-4 pt-4 pb-2">Upcoming Events</div>
              <v-data-table
                :headers="upcomingEventsHeaders"
                :items="upcomingEvents"
                item-key="id"
                :items-per-page="5"
                density="compact"
              />
            </v-card>
          </v-col>
        </v-row>

        <!-- Event Summary -->
        <v-row class="mt-3">
          <v-col cols="12">
            <v-card variant="outlined">
              <div class="text-subtitle-1 font-weight-medium px-4 pt-4 pb-2">Event Summary</div>
              <v-data-table
                :headers="eventSummaryHeaders"
                :items="eventSummary"
                :group-by="groupBy"
                item-key="id"
                :items-per-page="10"
                show-group-by
                :expanded="expanded"
                density="compact"
              />
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </v-sheet>
</template>

<script>
import axios from "axios";

const BAR_COLORS = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#00796B"];

export default {
  data() {
    return {
      loading: false,
      cards: [],
      eventSummary: [],
      upcomingEvents: [],
      topEvent: [],
      expanded: [],
      groupBy: [{ key: "name", order: "asc" }],
      eventSummaryHeaders: [
        { title: "Event Name",      value: "name" },
        { title: "Gender",          value: "gender" },
        { title: "Total Attendees", value: "total_attendees" },
        { title: "Checked In",      value: "checked_in" },
      ],
      upcomingEventsHeaders: [
        { title: "Event Name", value: "name" },
        { title: "Date",       value: "date" },
      ],
    };
  },
  computed: {
    genderData() {
      const maleCard   = this.cards.find(c => c.title === "Total Males");
      const femaleCard = this.cards.find(c => c.title === "Total Females");
      const males   = maleCard?.count   || 0;
      const females = femaleCard?.count || 0;
      const total = males + females;
      const circumference = 2 * Math.PI * 70;
      if (total === 0) {
        return { total: 0, maleDash: 0, femaleDash: 0, malePct: 0, femalePct: 0, circumference };
      }
      return {
        total, males, females, circumference,
        maleDash:   (males   / total) * circumference,
        femaleDash: (females / total) * circumference,
        malePct:   Math.round((males   / total) * 100),
        femalePct: Math.round((females / total) * 100),
      };
    },
    topEventChart() {
      if (!this.topEvent.length) return [];
      const n   = this.topEvent.length;
      const max = Math.max(...this.topEvent.map(e => e.total_attendees), 1);
      const gap = 12;
      const pad = 20;
      const totalWidth = 500;
      const barW = (totalWidth - pad * 2 - gap * (n - 1)) / n;
      const chartH = 126;
      return this.topEvent.map((ev, i) => {
        const h = Math.max(4, (ev.total_attendees / max) * chartH);
        return {
          x: pad + i * (barW + gap),
          y: 156 - h,
          w: barW,
          h,
          count: ev.total_attendees,
          label: ev.name.length > 12 ? ev.name.substring(0, 11) + "…" : ev.name,
          color: BAR_COLORS[i % BAR_COLORS.length],
        };
      });
    },
    eventStats() {
      const map = {};
      for (const row of this.eventSummary) {
        if (!map[row.id]) {
          map[row.id] = { id: row.id, name: row.name, total: 0, checkedIn: 0 };
        }
        map[row.id].total    += row.total_attendees || 0;
        map[row.id].checkedIn += Number(row.checked_in) || 0;
      }
      return Object.values(map).map(e => ({
        ...e,
        rate: e.total > 0 ? Math.round((e.checkedIn / e.total) * 100) : 0,
      }));
    },
  },
  methods: {
    getDashboardData() {
      this.loading = true;
      axios
        .get("/api/dashboard")
        .then((response) => {
          this.cards        = response.data.cards;
          this.eventSummary = response.data.eventResults;
          this.upcomingEvents = response.data.stats.upcoming_events;
          this.topEvent       = response.data.stats.top_events;
        })
        .catch((error) => {
          console.error("Error fetching dashboard data:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.getDashboardData();
  },
  watch: {
    eventSummary: {
      handler(val) {
        this.expanded = [...new Set(val.map(item => item.name))];
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.v-main {
  padding-left: 0 !important;
}
</style>
